import type { Theme } from "@mui/material/styles";

export type CoverageGradeTier =
  "super" | "good" | "ok" | "mid" | "poor" | "critical";

export function coverageTierForPercentage(
  percentage: number,
): CoverageGradeTier {
  const p = percentage;
  if (p > 85) return "super";
  if (p > 60) return "good";
  if (p > 40) return "ok";
  if (p > 20) return "mid";
  if (p > 10) return "poor";
  return "critical";
}

export function coverageTierFillSx(
  tier: CoverageGradeTier,
  t: Theme,
): { bgcolor: string; color: string } {
  switch (tier) {
    case "super":
      return {
        bgcolor: t.palette.success.dark,
        color: t.palette.common.white,
      };
    case "good":
      return {
        bgcolor: t.palette.success.light,
        color: t.palette.success.contrastText,
      };
    case "ok":
      return {
        bgcolor: t.palette.warning.dark,
        color: t.palette.getContrastText(t.palette.warning.dark),
      };
    case "mid":
      return {
        bgcolor: t.palette.warning.light,
        color: t.palette.getContrastText(t.palette.warning.light),
      };
    case "poor":
      return {
        bgcolor: t.palette.error.dark,
        color: t.palette.error.contrastText,
      };
    default:
      return {
        bgcolor: t.palette.error.dark,
        color: t.palette.common.white,
      };
  }
}

/** Kratki status kao u tablici statistike. */
export function coverageTierStatusLabel(tier: CoverageGradeTier): string {
  switch (tier) {
    case "super":
      return "Super";
    case "good":
      return "Solid";
    case "ok":
      return "Dobar";
    case "mid":
      return "Mid";
    case "poor":
      return "Loš";
    default:
      return "Krit";
  }
}

export function coverageTierStatusTooltip(tier: CoverageGradeTier): string {
  switch (tier) {
    case "super":
      return "Pokrivenost iznad 85%";
    case "good":
      return "Pokrivenost od 61% do 85%";
    case "ok":
      return "Pokrivenost od 41% do 60%";
    case "mid":
      return "Pokrivenost od 21% do 40%";
    case "poor":
      return "Pokrivenost od 11% do 20%";
    default:
      return "Pokrivenost 10% ili manje";
  }
}

const CHIP_TYPOGRAPHY = {
  fontSize: { xs: "9px", sm: "10px" } as const,
  fontWeight: 700,
  textTransform: "uppercase" as const,
};

export function coverageModuleStatusChip(
  percentage: number,
  t: Theme,
): { label: string; tooltip: string; sx: Record<string, unknown> } {
  const tier = coverageTierForPercentage(percentage);
  const fill = coverageTierFillSx(tier, t);
  const maxWidth = {
    super: { xs: 80, sm: "none" },
    good: { xs: 80, sm: "none" },
    ok: { xs: 72, sm: "none" },
    mid: { xs: 72, sm: "none" },
    poor: { xs: 64, sm: "none" },
    critical: { xs: 72, sm: "none" },
  }[tier];

  return {
    label: coverageTierStatusLabel(tier),
    tooltip: coverageTierStatusTooltip(tier),
    sx: {
      ...CHIP_TYPOGRAPHY,
      ...fill,
      maxWidth,
    },
  };
}

export function coverageAllianceCardPctChipSx(
  percentage: number,
  t: Theme,
): Record<string, unknown> {
  const tier = coverageTierForPercentage(percentage);
  return {
    ...coverageTierFillSx(tier, t),
  };
}

export function coverageAllianceTablePctTypographyColor(
  percentage: number,
  t: Theme,
): string {
  const tier = coverageTierForPercentage(percentage);
  switch (tier) {
    case "super":
      return t.palette.success.dark;
    case "good":
      return t.palette.success.light;
    case "ok":
      return t.palette.warning.dark;
    case "mid":
      return t.palette.warning.light;
    case "poor":
      return t.palette.error.main;
    default:
      return t.palette.error.dark;
  }
}
